import React, { useEffect } from 'react';
import { usePagination } from '@/shared/hooks/usePagination';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import { formatCurrency } from '@/shared/utils/formatter';
import { Mission, MissionTypeCode, PeriodCode, STATUS_LABEL } from '../types/promotion.types';
import { getMissionMeta, getPeriodName, buildEndedMissionAnalysis } from '../utils/promotionHelpers';
import '../styles/MissionList.css';

const ITEMS_PER_PAGE = 10;

interface MissionListProps {
    activeMissions: Mission[];
    endedMissions: Mission[];
    missionsLoading: boolean;
    activeCount: number;
    isReadOnly: boolean;
    missionTypeCodes: MissionTypeCode[];
    periodCodes: PeriodCode[];
    onRefresh: () => void;
    onSelectMission: (m: Mission) => void;
    onEditStart: (m: Mission) => void;
    onStatusToggle: (m: Mission) => void;
    onDelete: (seqNo: number) => void;
}

const MissionList: React.FC<MissionListProps> = ({
                                                     activeMissions,
                                                     endedMissions,
                                                     missionsLoading,
                                                     activeCount,
                                                     isReadOnly,
                                                     missionTypeCodes,
                                                     periodCodes,
                                                     onRefresh,
                                                     onSelectMission,
                                                     onEditStart,
                                                     onStatusToggle,
                                                     onDelete,
                                                 }) => {
    const {
        currentItems: activeItems,
        currentPage: activePage,
        totalPages: activeTotalPages,
        goToPage: goToActivePage,
        setCurrentPage: setActivePage,
    } = usePagination({ items: activeMissions, itemsPerPage: ITEMS_PER_PAGE });

    const {
        currentItems: endedItems,
        currentPage: endedPage,
        totalPages: endedTotalPages,
        goToPage: goToEndedPage,
        setCurrentPage: setEndedPage,
    } = usePagination({ items: endedMissions, itemsPerPage: ITEMS_PER_PAGE });

    // 데이터 갱신 시 1페이지로 복귀
    useEffect(() => { setActivePage(1); }, [activeMissions, setActivePage]);
    useEffect(() => { setEndedPage(1); }, [endedMissions, setEndedPage]);

    return (
        <>
            {/* ── 활성 미션 목록 ──────────────────────────────────────────────────── */}
            <div className="filter-section mission-list-section">
                <div className="mission-list-header">
                    <span className="mission-section-title">
                        <span className="mission-active-dot">●</span> 활성 미션 ({activeCount}건
                        {activeMissions.length > activeCount
                            ? ` / 비활성 ${activeMissions.length - activeCount}건`
                            : ''}
                        )
                    </span>
                    <button type="button" className="mission-refresh-btn" onClick={onRefresh}>
                        <i className="fas fa-sync-alt" /> 새로고침
                    </button>
                </div>

                {missionsLoading ? (
                    <div className="mission-empty">
                        <i className="fas fa-spinner fa-spin" />
                        <p>미션 목록을 불러오는 중...</p>
                    </div>
                ) : activeMissions.length === 0 ? (
                    <div className="mission-empty">
                        <i className="fas fa-inbox" />
                        <p>활성 미션이 없습니다.</p>
                    </div>
                ) : (
                    <div className="table-container">
                        <table className="supplier-selection-table">
                            <thead>
                            <tr>
                                <th>미션 제목</th>
                                <th>유형</th>
                                <th>기간</th>
                                <th>시작일</th>
                                <th>종료일</th>
                                <th>목표 건수</th>
                                <th>보상 조건</th>
                                <th>대상 라이더</th>
                                <th>상태</th>
                                {!isReadOnly && <th>관리</th>}
                            </tr>
                            </thead>
                            <tbody>
                            {activeItems.map((m) => {
                                const typeMeta = getMissionMeta(m.missionType, missionTypeCodes);
                                const periodName = getPeriodName(m.period, periodCodes);
                                return (
                                    <tr
                                        key={m.id}
                                        className="mission-table-row-clickable"
                                        onClick={() => onSelectMission(m)}
                                    >
                                        <td
                                            style={{
                                                textAlign: 'left',
                                                maxWidth: 200,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {m.title}
                                        </td>
                                        <td>
                                                <span
                                                    className={`mission-type-badge badge-${m.missionType.toLowerCase()}`}
                                                >
                                                    {typeMeta.name}
                                                </span>
                                        </td>
                                        <td>{periodName}</td>
                                        <td>{m.startDate}</td>
                                        <td>{m.endDate}</td>
                                        <td>{m.targetCount.toLocaleString()}건</td>
                                        <td>
                                            {formatCurrency(m.rewardAmount)}
                                            {typeMeta.rewardUnit}
                                        </td>
                                        <td>
                                            {m.targetType === 'ALL'
                                                ? '전체'
                                                : `${m.targetRiders.length}명`}
                                        </td>
                                        <td>
                                                <span
                                                    className={`mission-status-badge status-${m.status.toLowerCase()}`}
                                                >
                                                    {STATUS_LABEL[m.status]}
                                                </span>
                                        </td>
                                        {!isReadOnly && (
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div className="mission-table-actions">
                                                    <button
                                                        type="button"
                                                        className="mission-action-btn mission-action-edit"
                                                        onClick={() => onEditStart(m)}
                                                    >
                                                        수정
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`mission-action-btn ${
                                                            m.status === 'ACTIVE'
                                                                ? 'mission-action-deactivate'
                                                                : 'mission-action-activate'
                                                        }`}
                                                        onClick={() => onStatusToggle(m)}
                                                    >
                                                        {m.status === 'ACTIVE' ? '비활성' : '활성화'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="mission-action-btn mission-action-delete"
                                                        onClick={() => onDelete(m.seqNo)}
                                                    >
                                                        삭제
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                )}

                <Pagination
                    currentPage={activePage}
                    totalPages={activeTotalPages}
                    onPageChange={goToActivePage}
                />
            </div>

            {/* ── 종료된 미션 목록 ────────────────────────────────────────────────── */}
            <div className="filter-section mission-list-section">
                <div className="mission-list-header">
                    <span className="mission-section-title">
                        <span className="mission-ended-dot">●</span> 종료된 미션 ({endedMissions.length}건)
                    </span>
                </div>

                {endedMissions.length === 0 ? (
                    <div className="mission-empty">
                        <i className="fas fa-history" />
                        <p>종료된 미션이 없습니다.</p>
                    </div>
                ) : (
                    <div className="ended-mission-list">
                        {endedItems.map((m) => {
                            const typeMeta = getMissionMeta(m.missionType, missionTypeCodes);
                            const analysis = buildEndedMissionAnalysis(m);
                            return (
                                <div
                                    key={m.id}
                                    className="ended-mission-item"
                                    onClick={() => onSelectMission(m)}
                                >
                                    <div className="ended-mission-left">
                                        <span
                                            className={`mission-type-badge badge-${m.missionType.toLowerCase()}`}
                                        >
                                            {typeMeta.name}
                                        </span>
                                        <span className="ended-mission-title">{m.title}</span>
                                        <span className="ended-mission-date">
                                            {m.startDate} ~ {m.endDate}
                                        </span>
                                    </div>
                                    <div className="ended-mission-analysis">
                                        <span className="ended-analysis-chunk">
                                            총 대상자 - <strong>{analysis.totalTargets}</strong>
                                            {' / '}
                                            지급액 - <strong>{analysis.totalPayout}</strong>
                                        </span>
                                        {!analysis.isOneDay && (
                                            <>
                                                <span className="ended-analysis-sep">||</span>
                                                <span className="ended-analysis-chunk">
                                                    일 대상자 - <strong>{analysis.dailyTargets}</strong>
                                                    {' / '}일 평균 지급액 -{' '}
                                                    <strong>{analysis.dailyPayout}</strong>
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <Pagination
                    currentPage={endedPage}
                    totalPages={endedTotalPages}
                    onPageChange={goToEndedPage}
                />
            </div>
        </>
    );
};

export default MissionList;
